import axios from "axios";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import PrintableOrderDetails from "./PrintOrderDetails";
import { Loader2 } from "lucide-react"; // Import the loader icon

interface OrderDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  order: any;
  branchId: string;
  onStatusChange: (status: string) => void;
  children?: React.ReactNode;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({
  isOpen,
  onClose,
  order,
  branchId,
  onStatusChange,
  children,
}) => {
  const componentRef = useRef(null);
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState(
    order.fulfillment_status,
  );
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_APP_API_BASE_URL}print/order/`,
        {
          order_id: order.id,
          branch_id: branchId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      );

      if (response.status === 200) {
        toast.success(t("back.management.order.printSuccess"));
      }
    } catch (error: any) {
      console.error("Print error:", error);
      toast.error(
        error.response?.data?.error || t("back.management.order.printError"),
      );
    } finally {
      setIsPrinting(false);
    }
  };

  if (!isOpen) return null;

  const handleStatusChangeAndClose = () => {
    onStatusChange(selectedStatus);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl rounded-lg bg-white p-6">
        <button
          onClick={onClose}
          className="absolute right-0 top-0 p-4 text-gray-500 hover:text-gray-700"
        >
          &times;
        </button>
        <h2 className="mb-4 text-xl font-bold">
          {t("back.management.order.orderdetail")}
        </h2>
        <div className="max-h-60 overflow-y-auto">
          <div ref={componentRef}>
            <PrintableOrderDetails id={order.id} />
          </div>
        </div>
        <div className="mt-4">
          <label
            htmlFor="status"
            className="block text-sm font-medium text-gray-700"
          >
            {t("back.management.order.changeStatus")}
          </label>
          <select
            id="status"
            name="status"
            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="Preparing">
              {t("back.management.order.preparing")}
            </option>
            <option value="Scheduled">
              {t("back.management.order.scheduled")}
            </option>
            <option value="Completed">
              {t("back.management.order.complete")}
            </option>
          </select>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleStatusChangeAndClose}
            className="mr-2 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
          >
            {t("back.management.order.changeStatus")}
          </button>
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className="flex items-center justify-center rounded bg-gray-500 px-4 py-2 text-white hover:bg-gray-600 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isPrinting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("back.management.order.printing")}
              </>
            ) : (
              t("back.management.order.print")
            )}
          </button>
        </div>
      </div>
      {children}
    </div>
  );
};

export default OrderDetails;
